import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { Select } from '@/components/Select'
import { ManagerPlayerContributionsModal } from '@/features/manager/components/ManagerPlayerContributionsModal'
import { LoadingScreen } from '@/components/LoadingScreen'
import {
  useCreateManagerSubleague,
  useManagerLeagueDetail,
  useManagerLeagueDashboard,
  useManagerNextLineup,
  useManagerRanking,
  useManagerPlayerRanking,
  useManagerRoundRanking,
  useManagerRounds,
  useManagerSubleagueRanking,
  useManagerSubleagueRoundRanking,
  useManagerSubleagues,
  useJoinManagerLeague,
  useShareManagerLeague,
  type ManagerLastRoundSquadPlayer,
  type ManagerLeagueDashboard,
  type ManagerLeagueDetail,
  type ManagerPlayer,
  type ManagerPlayerRankingPlayer,
  type ManagerRoundAvailability,
  type ManagerSubleague,
} from '@/features/manager/api'
import { useCurrentUser } from '@/features/user/api'
import { PouleTabs, type PouleTabItem } from '@/features/poules/components/PouleTabs'
import { translate, useTranslation } from '@/i18n'
import { formAccentColor, pointsAccentColor } from '@/lib/pointsColor'
import { shareOrCopy } from '@/lib/share'

type ManagerLeagueTab = 'overview' | 'ranking' | 'players'
type ManagerContributionTarget =
  | { kind: 'user'; id: number; name: string }
  | { kind: 'player'; id: number; name: string }

function getManagerLeagueTabs(t: (key: string) => string): PouleTabItem[] {
  return [
    { id: 'overview', label: t('Overzicht') },
    { id: 'ranking', label: t('Ranglijst') },
    { id: 'players', label: t('Spelers') },
  ]
}

export function ManagerLeaguePage() {
  const { t } = useTranslation()
  const { fantasyLeagueId } = useParams<{ fantasyLeagueId: string }>()
  const leagueId = Number(fantasyLeagueId)
  const validLeagueId = Number.isFinite(leagueId) ? leagueId : undefined
  const leagueDetailQuery = useManagerLeagueDetail(validLeagueId)
  const dashboardQuery = useManagerLeagueDashboard(validLeagueId)
  const joinLeague = useJoinManagerLeague(validLeagueId)
  const currentUserQuery = useCurrentUser(true)
  const [activeTab, setActiveTab] = useState<ManagerLeagueTab>('overview')
  const [selectedRoundId, setSelectedRoundId] = useState<number>()
  const [selectedSubleagueId, setSelectedSubleagueId] = useState<number>()
  const [playerSearchInput, setPlayerSearchInput] = useState('')
  const [playerSearch, setPlayerSearch] = useState('')
  const [playerSlot, setPlayerSlot] = useState('')
  const [playerPage, setPlayerPage] = useState(1)
  const data = dashboardQuery.data
  const nextLineupQuery = useManagerNextLineup(data?.league.id)
  const roundsQuery = useManagerRounds(validLeagueId, activeTab === 'ranking')
  const subleaguesQuery = useManagerSubleagues(validLeagueId)
  const overallRankingQuery = useManagerRanking(
    validLeagueId,
    activeTab === 'ranking' && selectedRoundId == null,
  )
  const roundRankingQuery = useManagerRoundRanking(
    validLeagueId,
    selectedRoundId,
    activeTab === 'ranking' && selectedRoundId != null,
  )
  const subleagueRankingQuery = useManagerSubleagueRanking(
    validLeagueId,
    selectedSubleagueId,
    activeTab === 'ranking' && selectedSubleagueId != null && selectedRoundId == null,
  )
  const subleagueRoundRankingQuery = useManagerSubleagueRoundRanking(
    validLeagueId,
    selectedSubleagueId,
    selectedRoundId,
    activeTab === 'ranking' && selectedSubleagueId != null && selectedRoundId != null,
  )
  const playerRankingQuery = useManagerPlayerRanking(
    validLeagueId,
    playerSearch,
    playerSlot,
    playerPage,
    activeTab === 'players',
  )

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPlayerSearch((current) => {
        const next = playerSearchInput.trim()
        return current === next ? current : next
      })
      setPlayerPage(1)
    }, 350)

    return () => window.clearTimeout(timeoutId)
  }, [playerSearchInput])
  const leagueMembershipStatus = leagueDetailQuery.data?.league.membership?.status
  const needsJoin = Boolean(leagueDetailQuery.data && leagueMembershipStatus !== 'accepted')
  const isLeagueLoading = leagueDetailQuery.isLoading || dashboardQuery.isLoading

  return (
    <div className="space-y-6">
      <Link
        to="/dashboard/manager"
        className="inline-flex items-center gap-2 text-sm font-semibold text-surface-500 transition hover:text-surface-200"
      >
        <span aria-hidden="true">←</span>
        {t('Golero Manager')}
      </Link>

      {isLeagueLoading ? (
        <>
          <LoadingScreen compact />
          <LeagueSkeleton />
        </>
      ) : null}

      {needsJoin ? (
        <ManagerLeagueJoinState
          league={leagueDetailQuery.data!.league}
          isJoining={joinLeague.isPending}
          errorMessage={joinLeague.error?.message}
          onJoin={() => joinLeague.mutate()}
        />
      ) : null}

      {dashboardQuery.isError && !needsJoin ? (
        <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Golero Manager')}</p>
          <h1 className="mt-3 text-2xl font-black tracking-tight text-white">{t('Competitie niet geladen')}</h1>
          <p className="mt-2 text-sm leading-6 text-surface-400">
            {t('De gegevens van deze Manager-competitie konden niet worden opgehaald.')}
          </p>
          <button
            type="button"
            onClick={() => void dashboardQuery.refetch()}
            className="mt-5 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
          >
            {t('Opnieuw proberen')}
          </button>
        </section>
      ) : null}

      {!isLeagueLoading && data ? (
        <ManagerLeagueDashboardView
          data={data}
          competition={leagueDetailQuery.data?.league.competition}
          nextRound={nextLineupQuery.data?.lineup_flow?.app_round}
          activeTab={activeTab}
          tabs={getManagerLeagueTabs(t)}
          onTabChange={(nextTab) => {
            setActiveTab(nextTab as ManagerLeagueTab)
            if (nextTab === 'overview') setSelectedRoundId(undefined)
          }}
          currentUserId={currentUserQuery.data?.id}
          roundsQuery={roundsQuery}
          overallRankingQuery={overallRankingQuery}
          roundRankingQuery={roundRankingQuery}
          subleaguesQuery={subleaguesQuery}
          subleagueRankingQuery={subleagueRankingQuery}
          subleagueRoundRankingQuery={subleagueRoundRankingQuery}
          playerRankingQuery={playerRankingQuery}
          playerSearch={playerSearchInput}
          playerSlot={playerSlot}
          playerPage={playerPage}
          onPlayerSearch={setPlayerSearchInput}
          onPlayerSlot={(nextSlot) => {
            setPlayerSlot(nextSlot)
            setPlayerPage(1)
          }}
          onPlayerPage={setPlayerPage}
          selectedRoundId={selectedRoundId}
          onSelectRound={setSelectedRoundId}
          selectedSubleagueId={selectedSubleagueId}
          onSelectSubleague={setSelectedSubleagueId}
        />
      ) : null}
    </div>
  )
}

function ManagerLeagueJoinState({
  league,
  isJoining,
  errorMessage,
  onJoin,
}: {
  league: ManagerLeagueDetail['league']
  isJoining: boolean
  errorMessage: string | undefined
  onJoin: () => void
}) {
  const { t } = useTranslation()
  const isClosed = league.is_closed === true || league.enabled === false
  const requiresApproval = league.requires_approval === true

  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <CompetitionLogo src={league.competition?.logo_url ?? league.competition?.image_path} />
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Manager-competitie')}</p>
          <h1 className="mt-2 text-2xl font-black tracking-tight text-white">{league.name || t('Golero Manager')}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">
            {isClosed
              ? t('Deze Manager-league is gesloten voor nieuwe deelnemers.')
              : requiresApproval
                ? t('Stuur een aanvraag naar de beheerder. Na goedkeuring kun je je opstelling beheren.')
                : t('Doe mee aan deze Manager-league om je team samen te stellen en punten te verdienen.')}
          </p>
        </div>
      </div>
      {!isClosed ? (
        <button
          type="button"
          onClick={onJoin}
          disabled={isJoining || league.membership?.status === 'pending'}
          className="mt-6 rounded-xl bg-accent-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-wait disabled:opacity-60"
        >
          {league.membership?.status === 'pending'
            ? t('Aanvraag in afwachting')
            : isJoining
              ? t('Bezig…')
              : requiresApproval
              ? t('Aanvraag versturen')
              : t('Deelnemen aan league')}
        </button>
      ) : null}
      {errorMessage ? <p className="mt-3 text-sm text-red-300">{errorMessage}</p> : null}
    </section>
  )
}

function ManagerLeagueDashboardView({
  data,
  competition,
  nextRound,
  activeTab,
  tabs,
  onTabChange,
  currentUserId,
  roundsQuery,
  overallRankingQuery,
  roundRankingQuery,
  subleaguesQuery,
  subleagueRankingQuery,
  subleagueRoundRankingQuery,
  playerRankingQuery,
  playerSearch,
  playerSlot,
  playerPage,
  onPlayerSearch,
  onPlayerSlot,
  onPlayerPage,
  selectedRoundId,
  onSelectRound,
  selectedSubleagueId,
  onSelectSubleague,
}: {
  data: ManagerLeagueDashboard
  competition: ManagerLeagueDetail['league']['competition']
  nextRound: ManagerRoundAvailability['app_round'] | null | undefined
  activeTab: ManagerLeagueTab
  tabs: PouleTabItem[]
  onTabChange: (nextTab: string) => void
  currentUserId: number | undefined
  roundsQuery: ReturnType<typeof useManagerRounds>
  overallRankingQuery: ReturnType<typeof useManagerRanking>
  roundRankingQuery: ReturnType<typeof useManagerRoundRanking>
  subleaguesQuery: ReturnType<typeof useManagerSubleagues>
  subleagueRankingQuery: ReturnType<typeof useManagerSubleagueRanking>
  subleagueRoundRankingQuery: ReturnType<typeof useManagerSubleagueRoundRanking>
  playerRankingQuery: ReturnType<typeof useManagerPlayerRanking>
  playerSearch: string
  playerSlot: string
  playerPage: number
  onPlayerSearch: (value: string) => void
  onPlayerSlot: (value: string) => void
  onPlayerPage: (value: number) => void
  selectedRoundId: number | undefined
  onSelectRound: (roundId: number | undefined) => void
  selectedSubleagueId: number | undefined
  onSelectSubleague: (subleagueId: number | undefined) => void
}) {
  const { t } = useTranslation()
  const league = data.league
  const dashboard = data.dashboard
  const currentRound = dashboard.current_round
  // The dashboard round is the scoring context; the next-lineup round is the
  // round the Manager editor actually opens. Keep the management card aligned
  // with that editor instead of showing two different rounds.
  const activeRound = nextRound?.id ? nextRound : currentRound
  const targetRound = activeRound
  const bestPlayers = dashboard.best_players
  const shareLeague = useShareManagerLeague(league.id)
  const [shareState, setShareState] = useState<'idle' | 'success' | 'error'>('idle')
  const [isCreateSubleagueOpen, setIsCreateSubleagueOpen] = useState(false)
  const [isPlayerContributionsOpen, setIsPlayerContributionsOpen] = useState(false)
  const [contributionTarget, setContributionTarget] = useState<ManagerContributionTarget>()
  const [subleagueFeedback, setSubleagueFeedback] = useState<'idle' | 'success'>('idle')
  const canShareLeague = league.visibility === 'public' && league.enabled !== false && league.is_closed !== true
  const canCreateSubleague = subleaguesQuery.data?.meta.can_create_subleague === true || (league.enabled !== false && league.is_closed !== true)

  async function handleShareLeague() {
    if (shareLeague.isPending) return

    setShareState('idle')
    try {
      const share = await shareLeague.mutateAsync()
      await shareOrCopy({
        title: share.title,
        text: share.share_message,
        url: share.share_url,
      })
      setShareState('success')
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return
      setShareState('error')
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <CompetitionLogo src={competition?.logo_url ?? competition?.image_path} />
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Manager-competitie')}</p>
              <h1 className="mt-2 truncate text-3xl font-black tracking-tight text-white sm:text-4xl">
                {league.name || t('Golero Manager')}
              </h1>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
            <div className="flex flex-wrap justify-start gap-2 sm:justify-end">
              {canCreateSubleague ? (
                <button
                  type="button"
                  onClick={() => {
                    setSubleagueFeedback('idle')
                    setIsCreateSubleagueOpen(true)
                  }}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-white/[0.12] bg-white/[0.04] px-3.5 py-2 text-sm font-bold text-surface-200 transition hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-950"
                >
                  <span aria-hidden="true" className="text-lg leading-none">+</span>
                  {t('Subleague aanmaken')}
                </button>
              ) : null}
              {canShareLeague ? (
                <button
                  type="button"
                  onClick={() => void handleShareLeague()}
                  disabled={shareLeague.isPending}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-white/[0.14] bg-[#151a1c] px-4 py-2.5 text-sm font-bold text-white transition hover:border-accent-300/50 hover:bg-[#1b2325] disabled:cursor-wait disabled:opacity-60"
                >
                  <span aria-hidden="true">↗</span>
                  {shareLeague.isPending ? t('Link maken...') : t('Manager-league delen')}
                </button>
              ) : null}
            </div>
            {shareState === 'success' ? <span className="text-xs text-emerald-300">{t('Link gedeeld of gekopieerd.')}</span> : null}
            {shareState === 'error' ? <span className="text-xs text-red-300">{t('Delen is momenteel niet beschikbaar.')}</span> : null}
            {subleagueFeedback === 'success' ? <span className="text-xs text-emerald-300">{t('Subleague aangemaakt.')}</span> : null}
          </div>
        </div>
      </header>

      <PouleTabs items={tabs} activeTab={activeTab} onChange={onTabChange} />

      {activeTab === 'overview' ? (
        <div className="space-y-6">
          <section className="overflow-hidden rounded-[1.5rem] border border-accent-300/20 bg-[#0d1113] shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
            <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-accent-300/25 bg-accent-300/10 text-xl font-black text-accent-200" aria-hidden="true">
                  →
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent-200">{t('Actuele speelronde')}</p>
                  <h2 className="mt-1 text-2xl font-black tracking-tight text-white sm:text-3xl">{activeRound?.name || t('Nog geen ronde')}</h2>
                  <p className="mt-2 text-sm leading-6 text-surface-400">
                    {t('Beheer hier je opstelling voor de actuele speelronde.')}
                  </p>
                </div>
              </div>

              {targetRound?.id ? (
                <Link
                  to={`/dashboard/manager/competities/${league.id}/rondes/${targetRound.id}`}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-accent-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-accent-500"
                >
                  {t('Opstelling beheren')} <span aria-hidden="true">→</span>
                </Link>
              ) : null}
            </div>
          </section>

          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <div className="space-y-6">
              <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                  <LeagueStat value={dashboard.total_points ?? 0} label={t('Totaal punten')} />
                  <LeagueStat value={dashboard.current_round_points ?? 0} label={currentRound?.name || t('Deze ronde')} />
                  <LeagueStat value={dashboard.my_rank ? `#${dashboard.my_rank}` : '—'} label={t('Mijn ranking')} />
                  <LeagueStat value={dashboard.total_members ?? 0} label={t('Deelnemers')} />
                </div>
              </section>

              <LastRoundSquadCard
                players={dashboard.last_round_squad}
                onPlayerTap={(playerId, name) => {
                  setContributionTarget({ kind: 'player', id: playerId, name })
                  setIsPlayerContributionsOpen(true)
                }}
              />
            </div>

            <div className="space-y-6">
              <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
                <SectionHeading eyebrow={t('Prestaties')} title={t('Beste spelers')} />
                {bestPlayers?.total || bestPlayers?.current_round ? (
                  <div className="mt-5 space-y-4">
                    {bestPlayers.total ? (
                      <PlayerRow
                        player={bestPlayers.total}
                        label={t('Totaal')}
                        onPlayerTap={(playerId, name) => {
                          setContributionTarget({ kind: 'player', id: playerId, name })
                          setIsPlayerContributionsOpen(true)
                        }}
                      />
                    ) : null}
                    {bestPlayers.current_round ? (
                      <PlayerRow
                        player={bestPlayers.current_round}
                        label={currentRound?.name || t('Deze ronde')}
                        onPlayerTap={(playerId, name) => {
                          setContributionTarget({ kind: 'player', id: playerId, name })
                          setIsPlayerContributionsOpen(true)
                        }}
                      />
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-4 text-sm leading-6 text-surface-400">{t('Nog geen spelers met punten')}</p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setContributionTarget(undefined)
                    setIsPlayerContributionsOpen(true)
                  }}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-accent-200 transition hover:text-accent-100"
                >
                  {t('Bekijk alle spelers')} <span aria-hidden="true">→</span>
                </button>
              </section>

            </div>
          </div>
        </div>
      ) : activeTab === 'ranking' ? (
        <ManagerRankingTab
          currentUserId={currentUserId}
          roundsQuery={roundsQuery}
          overallRankingQuery={overallRankingQuery}
          roundRankingQuery={roundRankingQuery}
          subleagues={subleaguesQuery.data?.subleagues ?? []}
          subleagueRankingQuery={subleagueRankingQuery}
          subleagueRoundRankingQuery={subleagueRoundRankingQuery}
          selectedRoundId={selectedRoundId}
          onSelectRound={onSelectRound}
          selectedSubleagueId={selectedSubleagueId}
          onSelectSubleague={onSelectSubleague}
          onUserTap={(userId, name) => {
            setContributionTarget({ kind: 'user', id: userId, name })
            setIsPlayerContributionsOpen(true)
          }}
        />
      ) : (
        <ManagerPlayerRankingTab
          query={playerRankingQuery}
          search={playerSearch}
          slot={playerSlot}
          page={playerPage}
          onSearch={onPlayerSearch}
          onSlot={onPlayerSlot}
          onPage={onPlayerPage}
          onPlayerTap={(playerId, name) => {
            setContributionTarget({ kind: 'player', id: playerId, name })
            setIsPlayerContributionsOpen(true)
          }}
        />
      )}

      {isCreateSubleagueOpen ? (
        <ManagerSubleagueCreateSheet
          leagueId={league.id ?? 0}
          leagueName={league.name || t('Golero Manager')}
          onClose={() => setIsCreateSubleagueOpen(false)}
          onCreated={() => setSubleagueFeedback('success')}
        />
      ) : null}
      {isPlayerContributionsOpen ? (
        <ManagerPlayerContributionsModal
          leagueId={league.id ?? 0}
          targetUserId={contributionTarget?.kind === 'user' ? contributionTarget.id : undefined}
          targetUserName={contributionTarget?.kind === 'user' ? contributionTarget.name : undefined}
          initialPlayerId={contributionTarget?.kind === 'player' ? contributionTarget.id : undefined}
          initialPlayerName={contributionTarget?.kind === 'player' ? contributionTarget.name : undefined}
          onClose={() => {
            setIsPlayerContributionsOpen(false)
            setContributionTarget(undefined)
          }}
        />
      ) : null}
    </div>
  )
}

function ManagerSubleagueCreateSheet({
  leagueId,
  leagueName,
  onClose,
  onCreated,
}: {
  leagueId: number
  leagueName: string
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [isClosing, setIsClosing] = useState(false)
  const createSubleague = useCreateManagerSubleague(leagueId)

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !createSubleague.isPending) requestClose()
    }

    window.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [createSubleague.isPending])

  useEffect(() => {
    if (!isClosing) return
    const timeoutId = window.setTimeout(onClose, 300)
    return () => window.clearTimeout(timeoutId)
  }, [isClosing, onClose])

  function requestClose() {
    if (createSubleague.isPending) return
    setIsClosing(true)
  }

  async function handleSubmit() {
    const normalizedName = name.trim()
    if (normalizedName.length < 2) {
      createSubleague.reset()
      return
    }

    try {
      await createSubleague.mutateAsync(normalizedName)
      onCreated()
      requestClose()
    } catch {
      // The mutation error is rendered below.
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={requestClose}
        aria-label={t('Subleague aanmaken sluiten')}
        className={`${isClosing ? 'animate-drawer-backdrop-out' : 'animate-drawer-backdrop-in'} absolute inset-0 bg-black/80 backdrop-blur-sm`}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="manager-subleague-create-title"
        className={`${isClosing ? 'animate-manager-player-picker-out' : 'animate-manager-player-picker-in'} absolute inset-x-0 bottom-0 mx-auto flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-white/[0.07] p-5 sm:p-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Golero Manager')}</p>
            <h2 id="manager-subleague-create-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">{t('Subleague aanmaken')}</h2>
            <p className="mt-2 text-sm leading-6 text-surface-400">{t('Maak een subleague binnen {name}.', { name: leagueName })}</p>
          </div>
          <ModalCloseButton onClick={requestClose} label={t('Subleague aanmaken sluiten')} className="h-9 w-9" />
        </header>

        <form
          className="space-y-5 overflow-y-auto p-5 sm:p-6"
          onSubmit={(event) => {
            event.preventDefault()
            void handleSubmit()
          }}
        >
          <label className="block">
            <span className="text-sm font-bold text-white">{t('Subleague naam')}</span>
            <input
              autoFocus
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                if (createSubleague.isError) createSubleague.reset()
              }}
              disabled={createSubleague.isPending}
              maxLength={255}
              placeholder={t('Bijvoorbeeld: Vriendenleague')}
              className="mt-2 min-h-12 w-full rounded-xl border border-white/[0.12] bg-[#111719] px-4 py-3 text-sm font-semibold text-white outline-none transition placeholder:text-surface-500 focus:border-accent-400/70 focus:ring-2 focus:ring-accent-400/20 disabled:opacity-60"
            />
          </label>

          {name.trim().length === 1 ? <p className="text-sm text-red-300">{t('De subleague-naam moet minimaal 2 tekens bevatten.')}</p> : null}
          {createSubleague.isError ? <p role="alert" className="text-sm text-red-300">{createSubleague.error?.message || t('De subleague kon niet worden aangemaakt.')}</p> : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button type="button" onClick={requestClose} disabled={createSubleague.isPending} className="min-h-11 rounded-xl border border-white/[0.12] bg-[#151a1c] px-5 py-3 text-sm font-bold text-white transition hover:bg-white/[0.09] disabled:opacity-50">{t('Annuleren')}</button>
            <button type="submit" disabled={createSubleague.isPending || name.trim().length < 2} className="min-h-11 rounded-xl border border-white/[0.12] bg-white/[0.04] px-5 py-3 text-sm font-bold text-surface-200 transition hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d1113] disabled:cursor-wait disabled:opacity-60">
              {createSubleague.isPending ? t('Bezig…') : <><span className="mr-2 text-lg leading-none" aria-hidden="true">+</span>{t('Aanmaken')}</>}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function LeagueStat({ value, label }: { value: number | string; label: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-[#111719] px-3 py-3 sm:px-4 sm:py-4">
      <p className="truncate text-xl font-black text-white sm:text-2xl">{value}</p>
      <p className="mt-1 truncate text-[10px] font-semibold uppercase tracking-[0.1em] text-surface-500">{label}</p>
    </div>
  )
}

function ManagerRankingTab({
  currentUserId,
  roundsQuery,
  overallRankingQuery,
  roundRankingQuery,
  subleagues,
  subleagueRankingQuery,
  subleagueRoundRankingQuery,
  selectedRoundId,
  onSelectRound,
  selectedSubleagueId,
  onSelectSubleague,
  onUserTap,
}: {
  currentUserId: number | undefined
  roundsQuery: ReturnType<typeof useManagerRounds>
  overallRankingQuery: ReturnType<typeof useManagerRanking>
  roundRankingQuery: ReturnType<typeof useManagerRoundRanking>
  subleagues: ManagerSubleague[]
  subleagueRankingQuery: ReturnType<typeof useManagerSubleagueRanking>
  subleagueRoundRankingQuery: ReturnType<typeof useManagerSubleagueRoundRanking>
  selectedRoundId: number | undefined
  onSelectRound: (roundId: number | undefined) => void
  selectedSubleagueId: number | undefined
  onSelectSubleague: (subleagueId: number | undefined) => void
  onUserTap: (userId: number, name: string) => void
}) {
  const { t } = useTranslation()
  const rounds = roundsQuery.data?.rounds ?? []
  const selectedRound = rounds.find((round) => round.id === selectedRoundId)
  const isProvisional = selectedRound
    ? (selectedRound.matches_count ?? 0) > 0 &&
      (selectedRound.played_matches_count ?? selectedRound.completed_matches_count ?? 0) < (selectedRound.matches_count ?? 0)
    : rounds.some(
        (round) =>
          (round.matches_count ?? 0) > 0 &&
          (round.played_matches_count ?? round.completed_matches_count ?? 0) < (round.matches_count ?? 0),
      )
  const rankingQuery = selectedSubleagueId == null
    ? (selectedRoundId == null ? overallRankingQuery : roundRankingQuery)
    : (selectedRoundId == null ? subleagueRankingQuery : subleagueRoundRankingQuery)
  const rankingLoading = rankingQuery.isLoading
  const rankingError = rankingQuery.error
  const rankingRows = selectedRoundId == null
    ? ((selectedSubleagueId == null ? overallRankingQuery.data?.ranking : subleagueRankingQuery.data?.ranking) ?? []).map((entry) => ({
        userId: entry.user_id,
        rank: entry.rank ?? 0,
        name: entry.display_name || t('Onbekende speler'),
        image: entry.profile_image,
        points: `${entry.total_points ?? 0} pnt`,
        muted: false,
      }))
    : ((selectedSubleagueId == null ? roundRankingQuery.data?.ranking : subleagueRoundRankingQuery.data?.ranking) ?? []).map((entry) => ({
        userId: entry.user_id,
        rank: entry.rank ?? 0,
        name: entry.display_name || t('Onbekende speler'),
        image: entry.profile_image,
        points: entry.has_entry ? `${entry.points ?? 0} ${t('pnt')}` : t('Geen opstelling'),
        muted: !entry.has_entry,
      }))

  function retryRanking() {
    void roundsQuery.refetch()
    void rankingQuery.refetch()
  }

  return (
    <div className={subleagues.length > 0 ? 'space-y-4 pb-32' : 'space-y-4'}>
      <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
        <RankingRoundChip
          label={t('Totaal')}
          selected={selectedRoundId == null}
          onClick={() => onSelectRound(undefined)}
        />
        {rounds.map((round) => (
          <RankingRoundChip
            key={round.id}
            label={t('Ronde {sequence}', { sequence: round.sequence ?? '—' })}
            selected={selectedRoundId === round.id}
            onClick={() => round.id && onSelectRound(round.id)}
          />
        ))}
      </div>

      <div className={[
        'manager-round-status inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold',
        isProvisional
          ? 'manager-round-status-provisional border-amber-300/20 bg-amber-300/10 text-amber-200'
          : 'manager-round-status-final border-emerald-300/20 bg-emerald-300/10 text-emerald-200',
      ].join(' ')}>
        {isProvisional ? t('Voorlopig') : t('Definitief')}
      </div>

      <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-4 sm:p-5">
        {rankingLoading && rankingRows.length === 0 ? (
          <div className="flex min-h-56 items-center justify-center">
            <div className="h-1.5 w-40 animate-pulse rounded-full bg-accent-400/70" />
          </div>
        ) : null}

        {rankingError && rankingRows.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm font-bold text-white">{t('Ranking niet geladen')}</p>
            <p className="mt-2 text-sm text-surface-400">
              {rankingError instanceof Error ? rankingError.message : t('De ranking kon niet worden opgehaald.')}
            </p>
            <button
              type="button"
              onClick={retryRanking}
              className="mt-4 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
            >
              {t('Opnieuw proberen')}
            </button>
          </div>
        ) : null}

        {!rankingLoading && !rankingError && rankingRows.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm font-bold text-white">{t('Nog geen ranking')}</p>
            <p className="mt-2 text-sm text-surface-400">{t('Er zijn nog geen Manager-punten beschikbaar.')}</p>
          </div>
        ) : null}

        {rankingRows.length > 0 ? (
          <div className="divide-y divide-white/[0.07]">
            {rankingRows.map((row) => (
              <ManagerRankingRow
                key={`${row.userId}-${row.rank}`}
                rank={row.rank}
                name={row.name}
                image={row.image}
                points={row.points}
                muted={row.muted}
                isCurrentUser={row.userId === currentUserId}
                onClick={() => onUserTap(row.userId, row.name)}
              />
            ))}
          </div>
        ) : null}
      </section>

      {subleagues.length > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/[0.1] bg-[#0d1113]/95 px-4 py-3 shadow-[0_-12px_30px_rgba(0,0,0,0.22)] backdrop-blur-xl">
          <div className="mx-auto max-w-6xl">
            <label className="flex items-center gap-3 text-sm font-bold text-surface-300">
              <span className="shrink-0">{t('Ranglijst')}</span>
              <div className="min-w-0 flex-1">
                <Select
                  value={selectedSubleagueId == null ? '' : String(selectedSubleagueId)}
                  onChange={(value) => onSelectSubleague(value === '' ? undefined : Number(value))}
                  options={subleagues.map((subleague) => ({ value: String(subleague.id), label: subleague.name }))}
                  placeholder={t('Hoofdleague')}
                  ariaLabel={t('Ranglijst')}
                />
              </div>
            </label>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ManagerPlayerRankingTab({
  query,
  search,
  slot,
  page,
  onSearch,
  onSlot,
  onPage,
  onPlayerTap,
}: {
  query: ReturnType<typeof useManagerPlayerRanking>
  search: string
  slot: string
  page: number
  onSearch: (value: string) => void
  onSlot: (value: string) => void
  onPage: (value: number) => void
  onPlayerTap: (playerId: number, name: string) => void
}) {
  const { t } = useTranslation()
  const players = query.data?.players ?? []
  const pagination = query.data?.pagination
  const lastPage = pagination?.last_page ?? 1

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <label className="relative block">
          <span className="sr-only">{t('Zoek speler')}</span>
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-surface-600" aria-hidden="true">⌕</span>
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={t('Zoek speler')}
            className="w-full rounded-xl border border-white/[0.08] bg-[#111719] py-3 pl-9 pr-3 text-base text-white outline-none transition placeholder:text-surface-600 focus:border-accent-300/30 sm:text-sm"
          />
        </label>
        <div className="no-scrollbar flex min-w-0 gap-2 overflow-x-auto pb-1 sm:justify-end">
          {['', 'GK', 'DEF', 'MID', 'ATT'].map((option) => (
            <button
              key={option || 'all'}
              type="button"
              onClick={() => onSlot(option)}
              className={[
                'shrink-0 rounded-xl border px-3 py-2.5 text-xs font-bold transition',
                slot === option
                  ? 'border-accent-300/30 bg-accent-300/15 text-accent-100'
                  : 'border-white/[0.08] bg-[#111719] text-surface-400 hover:border-white/[0.16] hover:text-white',
              ].join(' ')}
            >
              {option || t('Alle posities')}
            </button>
          ))}
        </div>
      </div>

      {query.isFetching && players.length > 0 ? <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]"><div className="h-full w-1/3 animate-pulse rounded-full bg-accent-400" /></div> : null}

      <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-4 sm:p-5">
        {query.isLoading && players.length === 0 ? (
          <div className="flex min-h-56 items-center justify-center">
            <div className="h-1.5 w-40 animate-pulse rounded-full bg-accent-400/70" />
          </div>
        ) : null}

        {query.error && players.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm font-bold text-white">{t('Spelersranking niet geladen')}</p>
            <p className="mt-2 text-sm text-surface-400">
              {query.error instanceof Error ? query.error.message : t('De spelersranking kon niet worden opgehaald.')}
            </p>
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="mt-4 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
            >
              {t('Opnieuw proberen')}
            </button>
          </div>
        ) : null}

        {!query.isLoading && !query.error && players.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm font-bold text-white">{t('Geen spelers gevonden.')}</p>
          </div>
        ) : null}

        {players.length > 0 ? (
          <>
            <p className="mb-3 text-xs font-semibold text-surface-500">
              {pagination?.total ?? players.length} {t('Spelers gevonden').toLowerCase()}
            </p>
            <div className="divide-y divide-white/[0.07]">
              {players.map((player) => (
                <ManagerPlayerRankingRow
                  key={player.player_id}
                  player={player}
                  onClick={() => onPlayerTap(player.player_id, player.name || t('Onbekend'))}
                />
              ))}
            </div>
            {lastPage > 1 ? (
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/[0.07] pt-4">
                <button
                  type="button"
                  disabled={page <= 1 || query.isFetching}
                  onClick={() => onPage(Math.max(1, page - 1))}
                  className="rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2 text-xs font-bold text-surface-300 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('Vorige')}
                </button>
                <span className="text-xs font-semibold text-surface-500">
                  {page} / {lastPage}
                </span>
                <button
                  type="button"
                  disabled={page >= lastPage || query.isFetching}
                  onClick={() => onPage(Math.min(lastPage, page + 1))}
                  className="rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2 text-xs font-bold text-surface-300 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('Volgende')}
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </div>
  )
}

function ManagerPlayerRankingRow({
  player,
  onClick,
}: {
  player: ManagerPlayerRankingPlayer
  onClick: () => void
}) {
  const { t } = useTranslation()
  const rankTone = player.rank === 1
    ? 'manager-rank-tone-1 border-amber-300/40 bg-amber-300/15 text-amber-100'
    : player.rank === 2
      ? 'manager-rank-tone-2 border-slate-200/35 bg-slate-200/10 text-slate-100'
      : player.rank === 3
        ? 'manager-rank-tone-3 border-orange-300/35 bg-orange-300/10 text-orange-100'
        : 'manager-rank-tone-default border-white/[0.08] bg-[#111719] text-white'
  const meta = [
    player.team_display_name || player.team_name,
    player.position_group,
    player.jersey_number ? `#${player.jersey_number}` : null,
  ].filter(Boolean).join(' • ')

  return (
    <button
      type="button"
      onClick={onClick}
      className="manager-ranking-row flex w-full items-center gap-3 px-1 py-3.5 text-left transition hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 first:pt-1 last:pb-1"
      aria-label={t('Puntenopbouw van {name}', { name: player.name || t('Onbekend') })}
    >
      <span className={['flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-black', rankTone].join(' ')}>
        {player.rank || '—'}
      </span>
      <UserAvatar src={player.image_path} name={player.name} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-white">{player.name || t('Onbekend')}</p>
        {meta ? <p className="mt-1 truncate text-xs text-surface-500">{meta}</p> : null}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-black text-accent-200">{player.total_points}</p>
        <p className="text-[10px] uppercase tracking-[0.12em] text-surface-600">{t('punten')}</p>
      </div>
      <span aria-hidden="true" className="shrink-0 text-lg text-surface-500">→</span>
    </button>
  )
}

function RankingRoundChip({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'manager-ranking-round-chip shrink-0 rounded-full border px-3.5 py-2 text-xs font-bold transition',
        selected
          ? 'manager-ranking-round-chip-active border-accent-300/35 bg-accent-300/15 text-accent-100'
          : 'border-white/[0.08] bg-white/[0.04] text-surface-300 hover:border-white/[0.16] hover:text-white',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

function ManagerRankingRow({
  rank,
  name,
  image,
  points,
  muted,
  isCurrentUser,
  onClick,
}: {
  rank: number
  name: string
  image: string | null | undefined
  points: string
  muted: boolean
  isCurrentUser: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  const rankTone = rank === 1
    ? 'manager-rank-tone-1 border-amber-300/40 bg-amber-300/15 text-amber-100'
    : rank === 2
      ? 'manager-rank-tone-2 border-slate-200/35 bg-slate-200/10 text-slate-100'
      : rank === 3
        ? 'manager-rank-tone-3 border-orange-300/35 bg-orange-300/10 text-orange-100'
        : 'manager-rank-tone-default border-white/[0.08] bg-[#111719] text-white'

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'manager-ranking-row flex w-full items-center gap-3 px-1 py-3.5 text-left transition hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 first:pt-1 last:pb-1',
        isCurrentUser ? 'manager-ranking-row-current bg-accent-300/[0.06] px-3' : '',
      ].join(' ')}
      aria-label={t('Punten van {name}', { name })}
    >
      <span className={['flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-black', rankTone].join(' ')}>
        {rank || '—'}
      </span>
      <UserAvatar src={image} name={name} />
      <p className={['min-w-0 flex-1 truncate text-sm', isCurrentUser || rank <= 3 ? 'font-bold' : 'font-semibold', 'text-white'].join(' ')}>
        {name}{isCurrentUser ? ` (${t('jij')})` : ''}
      </p>
      <span className={[
        'shrink-0 text-sm',
        muted ? 'font-semibold italic text-surface-600' : isCurrentUser ? 'font-black text-accent-200' : 'font-black text-white',
      ].join(' ')}>
        {points}
      </span>
      <span aria-hidden="true" className="shrink-0 text-lg text-surface-500">→</span>
    </button>
  )
}

function PlayerRow({
  player,
  label,
  onPlayerTap,
}: {
  player: ManagerPlayer
  label: string
  onPlayerTap?: ((playerId: number, name: string) => void) | undefined
}) {
  const { t } = useTranslation()
  const teamName = player.team_display_name || player.team_name
  const playerId = player.player_id

  const content = (
    <>
      <UserAvatar src={player.image_path} name={player.name} />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-surface-600">{label}</p>
        <p className="mt-1 truncate text-sm font-bold text-white">{player.name || t('Speler')}</p>
        {teamName ? <p className="mt-0.5 truncate text-xs text-surface-500">{teamName}</p> : null}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-lg font-black text-white">{player.points ?? 0}</p>
        <p className="text-[10px] uppercase tracking-[0.12em] text-surface-600">{t('punten')}</p>
      </div>
    </>
  )

  if (onPlayerTap && playerId) {
    return (
      <button
        type="button"
        onClick={() => onPlayerTap(playerId, player.name || t('Speler'))}
        className="flex w-full items-center gap-3 border-b border-white/[0.07] pb-4 text-left last:border-0 last:pb-0 hover:bg-white/[0.03]"
      >
        {content}
      </button>
    )
  }

  return (
    <div className="flex items-center gap-3 border-b border-white/[0.07] pb-4 last:border-0 last:pb-0">
      {content}
    </div>
  )
}

type LastRoundSquadView = 'last_round' | 'form'

function LastRoundSquadCard({
  players,
  onPlayerTap,
}: {
  players: ManagerLastRoundSquadPlayer[]
  onPlayerTap?: ((playerId: number, name: string) => void) | undefined
}) {
  const { t } = useTranslation()
  const [view, setView] = useState<LastRoundSquadView>('last_round')

  if (players.length === 0) {
    return null
  }

  return (
    <div>
      <div className="flex justify-center">
        <div className="flex rounded-full border border-white/[0.08] bg-[#111719] p-0.5 text-[11px] font-bold">
          <button
            type="button"
            onClick={() => setView('last_round')}
            className={`rounded-full px-3 py-1 transition ${
              view === 'last_round' ? 'bg-accent-500 text-white' : 'text-surface-400 hover:text-white'
            }`}
          >
            {t('Laatste ronde')}
          </button>
          <button
            type="button"
            onClick={() => setView('form')}
            className={`rounded-full px-3 py-1 transition ${
              view === 'form' ? 'bg-accent-500 text-white' : 'text-surface-400 hover:text-white'
            }`}
          >
            {t('Vorm')}
          </button>
        </div>
      </div>
      <LastRoundSquadPitch players={players} view={view} onPlayerTap={onPlayerTap} />
      <p className="mt-2 px-2 text-center text-xs leading-5 text-surface-500">
        {t('De vorm toont de punten van de speler per ronde. Een uitgegrijsde ronde betekent dat je hem niet in je opstelling had.')}
      </p>
    </div>
  )
}

function LastRoundSquadPitch({
  players,
  view,
  onPlayerTap,
}: {
  players: ManagerLastRoundSquadPlayer[]
  view: LastRoundSquadView
  onPlayerTap?: ((playerId: number, name: string) => void) | undefined
}) {
  const { t } = useTranslation()
  const valueOf = (player: ManagerLastRoundSquadPlayer) => (view === 'form' ? player.form_points ?? 0 : player.points)
  const sorted = [...players].sort((a, b) => valueOf(b) - valueOf(a))
  const colorByPlayerId = new Map(
    sorted.map((player, index) => [
      player.player_id,
      formAccentColor(sorted.length > 1 ? index / (sorted.length - 1) : 0),
    ]),
  )
  const groups = groupSquadBySlot(players)

  return (
    <div className="relative mt-3 min-h-[26rem] overflow-hidden rounded-[1.25rem] border border-emerald-200/10 bg-[#0b3028]">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-x-5 top-5 bottom-5 rounded-xl border border-white/15" />
        <div className="absolute inset-x-5 top-1/2 h-px bg-white/15" />
        <div className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15" />
        <div className="absolute left-1/2 top-5 h-20 w-36 -translate-x-1/2 rounded-b-xl border-x border-b border-white/15" />
        <div className="absolute bottom-5 left-1/2 h-20 w-36 -translate-x-1/2 rounded-t-xl border-x border-t border-white/15" />
      </div>

      <div className="relative flex min-h-[26rem] flex-col justify-between gap-4 px-2 py-6 sm:px-2 sm:py-8">
        {[...groups].reverse().map(([group, groupPlayers]) => (
          <div
            key={group}
            className="mx-auto grid min-w-0 justify-items-center"
            style={{
              width: `min(100%, calc(${groupPlayers.length * 6}rem + ${(groupPlayers.length - 1) * 0.5}rem))`,
              gridTemplateColumns: `repeat(${groupPlayers.length}, minmax(0, 1fr))`,
              columnGap: '0.5rem',
              justifyContent: 'center',
            }}
          >
            {groupPlayers.map((player) => {
              const color = colorByPlayerId.get(player.player_id) ?? '#3fc989'
              const content = (
                <>
                  <span className="relative block">
                    <UserAvatar src={player.image_path} name={player.name} ring={color} />
                    <span
                      className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded-full px-1.5 py-px text-[10px] font-black text-black"
                      style={{ background: color }}
                    >
                      {valueOf(player)}
                    </span>
                  </span>
                  <span className="mt-2.5 w-full truncate text-center text-[10px] font-bold text-white sm:text-xs">
                    {player.name || t('Speler')}
                  </span>
                  <span className="w-full truncate text-center text-[9px] font-semibold uppercase tracking-[0.12em] text-white/50">
                    {player.club || player.slot || ''}
                  </span>
                  {view === 'form' ? (
                    <span className="mt-1 flex items-center justify-center gap-0.5">
                      {(player.recent_form ?? []).map((round) => (
                        <span
                          key={round.round_id}
                          className="flex h-4 w-4 items-center justify-center rounded-sm text-[8px] font-black leading-none text-black"
                          style={{
                            background: pointsAccentColor(round.points ?? 0),
                            opacity: round.has_entry ? 1 : 0.28,
                          }}
                          title={round.has_entry ? `${round.points ?? 0} pnt` : t('Niet opgesteld')}
                        >
                          {round.points ?? 0}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </>
              )

              return onPlayerTap ? (
                <button
                  key={player.player_id}
                  type="button"
                  onClick={() => onPlayerTap(player.player_id, player.name || t('Speler'))}
                  className="relative z-10 flex w-full min-w-0 max-w-24 flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-center transition hover:bg-white/[0.07]"
                >
                  {content}
                </button>
              ) : (
                <div
                  key={player.player_id}
                  className="relative z-10 flex w-full min-w-0 max-w-24 flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-center"
                >
                  {content}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

function groupSquadBySlot(players: ManagerLastRoundSquadPlayer[]): Array<[string, ManagerLastRoundSquadPlayer[]]> {
  const order = ['GK', 'DEF', 'MID', 'FWD', 'ATT']
  const groups = new Map<string, ManagerLastRoundSquadPlayer[]>()

  for (const player of players) {
    const key = (player.slot || '').toUpperCase() || '—'
    groups.set(key, [...(groups.get(key) ?? []), player])
  }

  return Array.from(groups.entries()).sort(([a], [b]) => {
    const rankA = order.indexOf(a)
    const rankB = order.indexOf(b)
    const normalizedA = rankA === -1 ? order.length : rankA
    const normalizedB = rankB === -1 ? order.length : rankB
    return normalizedA - normalizedB || a.localeCompare(b)
  })
}

function UserAvatar({
  src,
  name,
  ring,
}: {
  src: string | null | undefined
  name: string | null | undefined
  ring?: string
}) {
  const ringStyle = ring ? { boxShadow: `0 0 0 2px ${ring}` } : undefined

  return src ? (
    <img src={src} alt="" className="h-11 w-11 shrink-0 rounded-full bg-[#172024] object-cover" style={ringStyle} />
  ) : (
    <span
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#172024] text-sm font-bold text-accent-200"
      style={ringStyle}
    >
      {(name || 'S').slice(0, 1).toUpperCase()}
    </span>
  )
}

function CompetitionLogo({ src }: { src: string | null | undefined }) {
  return src ? <img src={src} alt="" className="h-14 w-14 shrink-0 object-contain sm:h-16 sm:w-16" /> : null
}

function SectionHeading({ eyebrow, title }: { eyebrow?: string | null | undefined; title: string }) {
  return (
    <div>
      {eyebrow ? (
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{eyebrow}</p>
      ) : null}
      <h2 className="mt-1 text-xl font-black tracking-tight text-white">{title}</h2>
    </div>
  )
}

function LeagueSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="space-y-6" aria-label={t('Competitie laden')}>
      <div className="h-20 animate-pulse rounded-2xl bg-[#0d1113]" />
      <div className="h-32 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      <div className="h-48 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
    </div>
  )
}
